import { type FilterChoice } from "shared/filter-where-clause";
import { type MapperInt } from "shared/mappers/database/MapperInt";
import { type CustomReportView } from "shared/mappers/database/reports/report";
export interface OrderByConfig {
  [key: string]: OrderByOption;
}

export interface OrderByOption {
  path: string;
  staticOrderType?: keyof typeof OrderTypes;
  additionalSorts?: OrderByOption[];
  default?: keyof typeof OrderTypes;
}

/**
 * Enum representing sort order directions.
 *
 * @enum {string}
 * @property {string} asc - Sort in ascending order. For example, sorting:
 *   - Numbers: `[3, 1, 2]` becomes `[1, 2, 3]`
 *   - Alphabetical: `['banana', 'apple', 'cherry']` becomes `['apple', 'banana', 'cherry']`
 *   - Dates: `['2025-05-01', '2023-01-01', '2024-01-01']` becomes `['2023-01-01', '2024-01-01', '2025-05-01']`
 *
 * @property {string} desc - Sort in descending order. For example, sorting:
 *   - Numbers: `[3, 1, 2]` becomes `[3, 2, 1]`
 *   - Alphabetical: `['banana', 'apple', 'cherry']` becomes `['cherry', 'banana', 'apple']`
 *   - Dates: `['2025-05-01', '2023-01-01', '2024-01-01']` becomes `['2025-05-01', '2024-01-01', '2023-01-01']`
 */
export enum OrderTypes {
  asc = "asc",
  desc = "desc",
}
export interface OrderByRequest {
  orderByKey?: string;
  orderType?: keyof typeof OrderTypes;
}
export interface PaginationRequest extends OrderByRequest {
  page: number;
  pageSize: number;
}
export type DynamicFilterChoices = {
  [key: string]: FilterChoice[];
};
export type PaginatedResults<
  RowGeneric = any,
  ExtraPropsGeneric = any | undefined,
> = {
  results: RowGeneric[];
  page: number;
  totalResults: number;
  pageSize: number;
  dynamicFilterChoices?: DynamicFilterChoices;
  extraProps?: ExtraPropsGeneric;
};

export type CustomReportResults<
  T,
  ExtraPropsT = Record<string, never>,
> = PaginatedResults<T, ExtraPropsT> & CustomReportView;

/**
 * A table page that the society's default report for the base table
 * returned in place of the page's own API (admin `apiDefaultReportOrContract`)
 */
export type DefaultReportTableResults<
  T,
  ExtraPropsT = Record<string, never>,
> = CustomReportResults<T, ExtraPropsT> & {
  /** Always true; tells the table the default report replaced its API */
  isDefaultReport: true;
};

/**
 * Whether a table page came from a society default report. Takes the page
 * itself rather than a props object, because a type predicate can only
 * narrow a named parameter.
 */
export const isDefaultReportTableResults = <T, ExtraPropsT>(
  results: PaginatedResults<T, ExtraPropsT> | undefined,
): results is DefaultReportTableResults<T, ExtraPropsT> =>
  !!results && "isDefaultReport" in results && results.isDefaultReport === true;

export const mapPaginatedResults = <
  InputType,
  OutputType,
  ExtraPropsInputType = Record<string, never>,
  ExtraPropsOutputType = Record<string, never>,
>(params: {
  results?: InputType[];
  paginationRequest?: PaginationRequest;
  paginatedResults?: PaginatedResults<InputType, ExtraPropsInputType>;
  totalResults?: number;
  resultsMapper: MapperInt<InputType, OutputType>;
  extraProps?: ExtraPropsInputType;
  extraPropsMapper?: (extraProps: ExtraPropsInputType) => ExtraPropsOutputType;
  dynamicFilterChoices?: DynamicFilterChoices;
}): PaginatedResults<OutputType, ExtraPropsOutputType> => {
  if (typeof params.totalResults !== "number" && !params.paginatedResults) {
    throw new Error("Either totalResults or paginatedResults must be provided");
  }
  if (!params.results && params.paginatedResults) {
    params.results = params.paginatedResults.results;
  }
  const mappedResults = params.results!.map(params.resultsMapper);

  if (!params.extraProps && params.paginatedResults?.extraProps) {
    //We need to pass the extra props to the extraPropsMapper if it exists
    params.extraProps = params.paginatedResults.extraProps;
  }

  // eslint-disable-next-line @typescript-eslint/no-unused-vars

  const mappedResponse: PaginatedResults<OutputType, ExtraPropsOutputType> = {
    dynamicFilterChoices:
      params.dynamicFilterChoices ??
      params.paginatedResults?.dynamicFilterChoices,
    page: params.paginationRequest?.page ?? params.paginatedResults?.page ?? 1,
    totalResults:
      params.totalResults ??
      params.paginatedResults?.totalResults ??
      params.results!.length,
    pageSize:
      params.paginationRequest?.pageSize ??
      params.paginatedResults?.pageSize ??
      params.results!.length,
    results: mappedResults,
    extraProps:
      params.extraPropsMapper && params.extraProps
        ? params.extraPropsMapper(params.extraProps)
        : (params.extraProps as ExtraPropsOutputType),
  };

  return mappedResponse;
};
